"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComponentSchema = void 0;
exports.migrateComponent = migrateComponent;
exports.migrateSignal = migrateSignal;
exports.migrateLegacyParameters = migrateLegacyParameters;
const BoundaryLevel_1 = require("../BoundaryLevel");
const SchFootprint_1 = require("./DrawingModels/SchFootprint");
const PcbFootprint_1 = require("./DrawingModels/PcbFootprint");
const Migration_1 = require("../Migration");
const MergeInferenceReports_1 = require("../MergeInferenceReports");
const zod_1 = require("zod");
const metadata_1 = require("./metadata");
const Signal_1 = require("./Signal");
const Parametrics_1 = require("./Parametrics/Parametrics");
const StandardComponentModel_1 = require("./StandardComponentModel");
exports.ComponentSchema = zod_1.z.object({
    name: zod_1.z.string(),
    numPins: zod_1.z.union([zod_1.z.number(), zod_1.z.number().array()]),
    numPads: zod_1.z.union([zod_1.z.number(), zod_1.z.number().array()]),
    metadata: metadata_1.MetadataSchema.optional(),
    parametrics: Parametrics_1.ParametricsSchema.optional(),
    footprints: PcbFootprint_1.PcbFootprintSchema.array(),
    symbols: SchFootprint_1.SchFootprintSchema.array(),
    signals: Signal_1.SignalSchema.array(),
    model: StandardComponentModel_1.StandardComponentModelSchema.optional(),
    isVirtual: zod_1.z.boolean().optional(),
    isMultiUnit: zod_1.z.boolean().optional(),
});
function migrateComponent(component) {
    const result = {
        type: Migration_1.MigrationResultType.ComponentMigration,
        isModified: false,
        entity: structuredClone(component),
        message: '',
        innerResults: [],
    };
    const signalsMigrationResult = migrateComponentSignals(component);
    if (signalsMigrationResult.isModified) {
        result.innerResults.push(signalsMigrationResult);
        result.isModified = true;
        result.entity.signals = component.signals.map((signal) => {
            const migratedSignal = signalsMigrationResult.entity.find((migratedSignal) => migratedSignal.id === signal.id);
            return migratedSignal ?? signal;
        });
    }
    if (result.isModified) {
        result.message = `${result.entity.name}: Migrated ${result.innerResults.length} properties`;
    }
    if (result.entity.model?.parameterInferenceReport) {
        result.entity.model.inferenceReport = (0, MergeInferenceReports_1.mergeInferenceReports)(result.entity.model.parameterInferenceReport, result.entity.model.inferenceReport);
        delete result.entity.model.parameterInferenceReport;
    }
    if (result.entity.model && 'parameters' in result.entity.model) {
        result.entity.model.inferenceReport = (0, MergeInferenceReports_1.mergeInferenceReports)(result.entity.model.inferenceReport, migrateLegacyParameters(result.entity.model.parameters));
        result.innerResults?.push({
            type: Migration_1.MigrationResultType.PropertyModified,
            message: 'Migrated legacy parameters to inference report',
            severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
        });
        delete result.entity.model.parameters;
        result.isModified = true;
    }
    return result;
}
function migrateComponentSignals(component) {
    const migratedSignals = component.signals
        .map((signal) => migrateSignal(signal))
        .filter((signal) => signal.isModified);
    const result = {
        type: Migration_1.MigrationResultType.PropertyModified,
        isModified: migratedSignals.length > 0,
        entity: migratedSignals.map((signal) => signal.entity),
        message: `Migrated ${migratedSignals.length} signals`,
        innerResults: migratedSignals,
    };
    return result;
}
function migrateSignal(signal) {
    const result = {
        type: Migration_1.MigrationResultType.SignalMigration,
        isModified: false,
        entity: structuredClone(signal),
        message: '',
        innerResults: [],
    };
    if (!signal.attributes) {
        result.entity.attributes = {};
        result.innerResults.push({
            type: Migration_1.MigrationResultType.PropertyAdded,
            message: 'Added attributes property',
            severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
        });
    }
    if (signal.signalType && ['input', 'output', 'biio'].includes(signal.signalType.toString())) {
        result.entity.signalDirection = signal.signalType.toString();
        result.innerResults.push({
            type: Migration_1.MigrationResultType.PropertyModified,
            message: `Migrated legacy signalType to signalDirection for value: ${signal.signalType.toString()}`,
            severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
        });
        delete result.entity.signalType;
        result.innerResults.push({
            type: Migration_1.MigrationResultType.PropertyDeleted,
            message: 'Removed deprecated signalType property',
            severity: BoundaryLevel_1.BoundaryLevel.Warning,
        });
        result.isModified = true;
    }
    if (Object.prototype.hasOwnProperty.call(signal, 'powerSignalType')) {
        const powerSignalType = signal['powerSignalType']?.toString() ?? '';
        if (powerSignalType.includes('gnd')) {
            result.entity.attributes.ground = true;
            result.innerResults.push({
                type: Migration_1.MigrationResultType.PropertyModified,
                message: `Migrated legacy powerSignalType '${powerSignalType}' to SignalAttribute ground`,
                severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
            });
        }
        else {
            result.entity.attributes.power = true;
            result.innerResults.push({
                type: Migration_1.MigrationResultType.PropertyModified,
                message: `Migrated legacy powerSignalType '${powerSignalType}' to SignalAttribute power`,
                severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
            });
        }
        delete result.entity.powerSignalType;
        result.innerResults.push({
            type: Migration_1.MigrationResultType.PropertyDeleted,
            message: 'Removed deprecated powerSignalType property',
            severity: BoundaryLevel_1.BoundaryLevel.Warning,
        });
        result.isModified = true;
    }
    if (signal.signalType && ['power'].includes(signal.signalType?.toString())) {
        if (!(result.entity.attributes?.ground || result.entity.attributes?.power)) {
            if (/gnd|ground/i.test(signal.name)) {
                result.entity.attributes.ground = true;
                result.innerResults.push({
                    type: Migration_1.MigrationResultType.PropertyModified,
                    message: `Migrated legacy signalType 'power' to SignalAttribute ground`,
                    severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
                });
            }
            else {
                result.entity.attributes.power = true;
                result.innerResults.push({
                    type: Migration_1.MigrationResultType.PropertyModified,
                    message: `Insufficient information to migrate legacy signalType 'power' to SignalAttribute power or ground`,
                    severity: BoundaryLevel_1.BoundaryLevel.Error,
                });
            }
        }
        delete result.entity.signalType;
        result.innerResults.push({
            type: Migration_1.MigrationResultType.PropertyDeleted,
            message: 'Removed deprecated signalType property',
            severity: BoundaryLevel_1.BoundaryLevel.Warning,
        });
        result.isModified = true;
    }
    if (result.isModified) {
        result.message = `Migrated signal: ${signal.name}`;
    }
    return result;
}
function migrateLegacyParameters(parameters) {
    return {
        parameters: Object.entries(parameters).reduce((acc, [key, value]) => {
            if (value === undefined || value === null) {
                return acc;
            }
            if (typeof value === 'object') {
                if ((value.value === undefined || value.value === null || value.value === '') &&
                    (value.variance === undefined || value.variance === null || value.variance === '')) {
                    return acc;
                }
            }
            acc[key] = {
                source: 'hil',
                value,
            };
            return acc;
        }, {}),
    };
}
//# sourceMappingURL=component.js.map